import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, X } from 'lucide-react';
import type { Booking } from '../api';
import { updateBooking } from '../api';
import { formatDatetime } from '../utils/dates';
import { useSettings } from '../context/SettingsContext';
import { Button, Avatar } from './ui';
import VehicleSearchField from './VehicleSearchField';
import RenterAutocomplete from './RenterAutocomplete';

interface Props {
  booking: Booking;
  onClose: () => void;
  onSaved: (updated: Partial<Booking>) => void;
}

function getInitials(name: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2);
  return (parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '');
}

// Convert MySQL datetime "2026-04-29 15:00:00" to <input type="datetime-local"> format "2026-04-29T15:00"
function toDtLocal(dt: string | null | undefined): string {
  if (!dt) return '';
  return dt.slice(0, 16).replace(' ', 'T');
}

function fromDtLocal(v: string): string {
  if (!v) return '';
  return v.replace('T', ' ') + ':00';
}

const inputCls =
  'w-full border border-ink-200 bg-cream-50 rounded-lg px-3 py-2 text-sm text-ink-900 placeholder:text-[#a89d8a] focus:outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-500/30 transition-colors';

export default function BookingEditModal({ booking: b, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const tz = settings.timezone || 'Europe/Copenhagen';

  const [editing, setEditing] = useState(false);
  const [vehicleId, setVehicleId] = useState<number | null>(b.vehicle_id);
  const [vehicleReg, setVehicleReg] = useState<string>(b.registration_number || '');
  const [renterId, setRenterId] = useState<number | null>(b.renter_id);
  const [renterName, setRenterName] = useState<string>(b.renter_name || '');
  const [renterPhone, setRenterPhone] = useState<string>(b.renter_phone || '');
  const [renterEmail, setRenterEmail] = useState<string>(b.renter_email || '');
  const [startsAt, setStartsAt] = useState<string>(toDtLocal(b.starts_at));
  const [endsAt, setEndsAt] = useState<string>(toDtLocal(b.ends_at));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setVehicleId(b.vehicle_id);
    setVehicleReg(b.registration_number || '');
    setRenterId(b.renter_id);
    setRenterName(b.renter_name || '');
    setRenterPhone(b.renter_phone || '');
    setRenterEmail(b.renter_email || '');
    setStartsAt(toDtLocal(b.starts_at));
    setEndsAt(toDtLocal(b.ends_at));
    setError(null);
  };

  const handleCancel = () => { reset(); setEditing(false); };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload: Parameters<typeof updateBooking>[1] = {};
      if (vehicleId !== b.vehicle_id) payload.vehicle_id = vehicleId;
      if (renterId !== b.renter_id) payload.renter_id = renterId;
      const newStart = fromDtLocal(startsAt);
      const newEnd   = fromDtLocal(endsAt);
      if (newStart && newStart !== b.starts_at) payload.starts_at = newStart;
      if (newEnd && newEnd !== b.ends_at) payload.ends_at = newEnd;

      await updateBooking(b.id, payload);

      const updated: Partial<Booking> = {
        ...payload,
        registration_number: vehicleReg,
        renter_name: renterName,
        renter_phone: renterPhone,
        renter_email: renterEmail,
      };
      onSaved(updated);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fejl');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4">
      <div className="bg-cream-50 rounded-card shadow-card-md w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-ink-100">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar initials={getInitials(b.renter_name)} size="lg" />
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-ink-900 tracking-tight truncate">
                {b.renter_name || '—'}
              </h2>
              <p className="text-xs text-ink-500 font-mono mt-0.5">
                {b.registration_number || '—'}
                {b.source && (
                  <span className="ml-2 text-ink-300 normal-case font-sans">
                    · {b.source === 'gomore' ? 'GoMore' : b.source}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 -mr-2 rounded-lg text-ink-300 hover:text-ink-700 hover:bg-cream-100 transition-colors"
            aria-label={t('modal.close', { defaultValue: 'Luk' })}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {error && (
            <div className="text-sm text-rose-700 bg-rose-100 px-3 py-2 rounded-lg">{error}</div>
          )}

          {/* Periode */}
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2">
              {t('rental.detail_rental', { defaultValue: 'Periode' })}
            </h3>
            {editing ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-ink-500 mb-1">{t('manual_booking.starts_at', { defaultValue: 'Starttidspunkt' })}</label>
                  <input
                    type="datetime-local"
                    value={startsAt}
                    onChange={e => setStartsAt(e.target.value)}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block text-xs text-ink-500 mb-1">{t('manual_booking.ends_at', { defaultValue: 'Sluttidspunkt' })}</label>
                  <input
                    type="datetime-local"
                    value={endsAt}
                    onChange={e => setEndsAt(e.target.value)}
                    className={inputCls}
                  />
                </div>
              </div>
            ) : (
              <div className="text-sm text-ink-900">
                <span className="font-mono">{formatDatetime(b.starts_at, tz)}</span>
                <span className="mx-2 text-ink-300">→</span>
                <span className="font-mono">{formatDatetime(b.ends_at, tz)}</span>
                {b.rental_duration_hours != null && (
                  <span className="block text-xs text-ink-500 mt-1">
                    {b.rental_duration_hours} timer
                  </span>
                )}
              </div>
            )}
          </section>

          {/* Bil */}
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2">
              {t('rentals.col_vehicle', { defaultValue: 'Bil' })}
            </h3>
            {editing ? (
              <div className="space-y-2">
                {vehicleReg && (
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-ink-900">{vehicleReg}</span>
                    <button
                      onClick={() => { setVehicleId(null); setVehicleReg(''); }}
                      className="text-xs text-ink-500 hover:text-rose-700"
                    >
                      Skift
                    </button>
                  </div>
                )}
                {!vehicleReg && (
                  <VehicleSearchField
                    placeholder={t('rentals.search_vehicle', { defaultValue: 'Søg bil...' })}
                    onSelect={v => { setVehicleId(v.id); setVehicleReg(v.registration_number); }}
                  />
                )}
              </div>
            ) : (
              <div className="text-sm">
                <span className="font-mono text-ink-900 font-semibold">{b.registration_number || '—'}</span>
                {!b.vehicle_id && (
                  <span className="ml-2 text-xs text-rose-700">(ikke tildelt)</span>
                )}
              </div>
            )}
          </section>

          {/* Lejer */}
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500 mb-2">
              {t('manual_booking.section_renter', { defaultValue: 'Lejer' })}
            </h3>
            {editing ? (
              <div className="grid grid-cols-1 gap-3">
                <RenterAutocomplete
                  label={t('manual_booking.renter_name', { defaultValue: 'Navn' })}
                  value={renterName}
                  onChange={v => { setRenterName(v); setRenterId(null); }}
                  onSelect={r => {
                    setRenterId(r.id);
                    setRenterName(r.name);
                    setRenterPhone(r.phone ?? '');
                    setRenterEmail(r.email ?? '');
                  }}
                />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-ink-500 mb-1">{t('manual_booking.renter_phone', { defaultValue: 'Telefon' })}</label>
                    <input type="text" value={renterPhone} onChange={e => setRenterPhone(e.target.value)} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs text-ink-500 mb-1">{t('manual_booking.renter_email', { defaultValue: 'Email' })}</label>
                    <input type="email" value={renterEmail} onChange={e => setRenterEmail(e.target.value)} className={inputCls} />
                  </div>
                </div>
              </div>
            ) : (
              <dl className="text-sm space-y-1">
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-500">Navn</dt>
                  <dd className="text-ink-900 font-medium">{b.renter_name || '—'}</dd>
                </div>
                {b.renter_phone && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-ink-500">Telefon</dt>
                    <dd className="text-ink-900 font-mono text-xs">{b.renter_phone}</dd>
                  </div>
                )}
                {b.renter_email && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-ink-500">Email</dt>
                    <dd className="text-ink-900 font-mono text-xs truncate">{b.renter_email}</dd>
                  </div>
                )}
              </dl>
            )}
          </section>

          {b.source === 'gomore' && editing && (
            <p className="text-xs text-ink-500 bg-cream-200 px-3 py-2 rounded-lg">
              ⚠ Bookingen synkroniseres fra GoMore. Manuelle ændringer kan blive overskrevet ved næste sync.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-ink-100 bg-cream-100">
          <div>
            {!editing && (
              <Button variant="ghost" size="sm" iconLeft={<Pencil size={13} />} onClick={() => setEditing(true)}>
                {t('rental.edit_btn', { defaultValue: 'Rediger' })}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {editing ? (
              <>
                <Button variant="ghost" size="sm" onClick={handleCancel} disabled={saving}>
                  {t('modal.cancel', { defaultValue: 'Annullér' })}
                </Button>
                <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? '…' : t('modal.save', { defaultValue: 'Gem' })}
                </Button>
              </>
            ) : (
              <Button variant="ghost" size="sm" onClick={onClose}>
                {t('modal.close', { defaultValue: 'Luk' })}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
